import React, { useRef } from 'react';
import {
  Chart as ChartJS,
  LineElement,
  PointElement,
  LinearScale,
  CategoryScale,
  Tooltip,
  Filler,
  Legend,
  Chart,
  LineController,
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import { formatCurrency } from '@utils/common';

ChartJS.register(
  LineController,
  LineElement,
  PointElement,
  LinearScale,
  CategoryScale,
  Tooltip,
  Filler,
  Legend,
);

const CombinedProjectionsChart = ({
  xLabel,
  yLabel,
  chartData,
}: {
  xLabel: string;
  yLabel: string;
  chartData: any;
}) => {
  const chartRef = useRef<Chart<'line'>>(null);

  const withoutFunding = chartData?.rows?.[0]
    ? Object.entries(chartData.rows[0])
        .filter(([key, value]) => /^\d{4}-\d{2}$/.test(key) && value != null)
        .map(([, value]) => Number((value as string).replace(/[$,]/g, '')))
    : [];

  const withFunding = chartData?.rows?.[1]
    ? Object.entries(chartData.rows[1])
        .filter(([key, value]) => /^\d{4}-\d{2}$/.test(key) && value != null)
        .map(([, value]) => Number((value as string).replace(/[$,]/g, '')))
    : [];

  const labels = chartData?.columns?.slice(1).map((col: any) => col.label) || [];

  const data = {
    labels,
    datasets: [
      {
        label: 'With PVX Funding',
        data: withFunding,
        borderColor: '#5EC6B5',
        backgroundColor: '#5EC6B5',
        pointRadius: 0,
        tension: 0,
        borderWidth: 2,
      },
      {
        label: 'Without PVX Funding',
        data: withoutFunding,
        borderColor: '#1C1C1C',
        backgroundColor: '#1C1C1C',
        pointRadius: 0,
        tension: 0,
        borderWidth: 2,
      },
    ],
  };

  const fillBetweenPlugin = {
    id: 'fillBetweenLines',
    beforeDatasetsDraw(chart: any) {
      const {
        ctx,
        chartArea: { top, bottom },
      } = chart;

      const meta1 = chart.getDatasetMeta(0);
      const meta2 = chart.getDatasetMeta(1);

      if (!meta1 || !meta2) return;

      ctx.save();

      ctx.beginPath();

      meta1.data.forEach((point: any, index: number) => {
        if (index === 0) {
          ctx.moveTo(point.x, point.y);
        } else {
          ctx.lineTo(point.x, point.y);
        }
      });

      for (let i = meta2.data.length - 1; i >= 0; i--) {
        const point = meta2.data[i];
        ctx.lineTo(point.x, point.y);
      }

      ctx.closePath();

      const gradient = ctx.createLinearGradient(0, top, 0, bottom);
      gradient.addColorStop(0, 'rgba(94, 198, 181, 0.4)');
      gradient.addColorStop(1, 'rgba(94, 198, 181, 0)');

      ctx.fillStyle = gradient;
      ctx.fill();

      ctx.restore();
    },
  };

  const customXAxisLabelPlugin = (xLabel: string, yLabel: string) => ({
    id: 'customXAxisLabelPlugin',
    afterDraw: (chart: any) => {
      const ctx = chart.ctx;
      const xAxis = chart.scales.x;

      // === X-Axis Label Drawing ===
      const label = xLabel;
      const labelX = (xAxis.left + xAxis.right) / 2;
      const labelY = xAxis.bottom + 20;

      const padding = 10;
      const borderRadius = 10;
      const arrowSize = 8;
      const arrowTailLength = 8;

      ctx.font = '700 12px Manrope';
      const textWidth = ctx.measureText(label).width;
      const labelWidth = textWidth + padding * 2;
      const labelHeight = 20;

      ctx.fillStyle = '#FFFFFF';
      ctx.strokeStyle = '#E8EAEB';
      ctx.lineWidth = 1;

      ctx.beginPath();
      ctx.moveTo(labelX - labelWidth / 2 + borderRadius, labelY - labelHeight / 2);
      ctx.lineTo(labelX + labelWidth / 2 - borderRadius + 25, labelY - labelHeight / 2);
      ctx.quadraticCurveTo(
        labelX + labelWidth / 2 + 25,
        labelY - labelHeight / 2,
        labelX + labelWidth / 2 + 25,
        labelY - labelHeight / 2 + borderRadius,
      );
      ctx.lineTo(labelX + labelWidth / 2 + 25, labelY + labelHeight / 2 - borderRadius);
      ctx.quadraticCurveTo(
        labelX + labelWidth / 2 + 25,
        labelY + labelHeight / 2,
        labelX + labelWidth / 2 - borderRadius + 25,
        labelY + labelHeight / 2,
      );
      ctx.lineTo(labelX - labelWidth / 2 + borderRadius, labelY + labelHeight / 2);
      ctx.quadraticCurveTo(
        labelX - labelWidth / 2,
        labelY + labelHeight / 2,
        labelX - labelWidth / 2,
        labelY + labelHeight / 2 - borderRadius,
      );
      ctx.lineTo(labelX - labelWidth / 2, labelY - labelHeight / 2 + borderRadius);
      ctx.quadraticCurveTo(
        labelX - labelWidth / 2,
        labelY - labelHeight / 2,
        labelX - labelWidth / 2 + borderRadius,
        labelY - labelHeight / 2,
      );
      ctx.closePath();
      ctx.fill();
      ctx.stroke();

      ctx.fillStyle = '#162C36';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(label, labelX, labelY + 1);

      // Arrow (X)
      const arrowX = labelX + labelWidth / 2 + 5;
      const arrowY = labelY - 1;
      ctx.beginPath();
      ctx.moveTo(arrowX - arrowTailLength, arrowY);
      ctx.lineTo(arrowX, arrowY);
      ctx.strokeStyle = '#000';
      ctx.lineWidth = 2;
      ctx.stroke();

      ctx.beginPath();
      ctx.moveTo(arrowX, arrowY - arrowSize / 2);
      ctx.lineTo(arrowX + arrowSize, arrowY);
      ctx.lineTo(arrowX, arrowY + arrowSize / 2);
      ctx.closePath();
      ctx.fillStyle = '#000';
      ctx.fill();

      // === Y-Axis Label Drawing ===
      const yAxis = chart.scales.y;
      const ctxY = chart.ctx;
      const labelText = yLabel;

      const textMetrics = ctxY.measureText(labelText);
      const labelBoxWidth = textMetrics.width + 20;
      const labelBoxHeight = 20;

      const labelCenterX = yAxis.left - 40;
      const labelCenterY = (yAxis.top + yAxis.bottom) / 2;

      ctxY.save();
      ctxY.translate(labelCenterX, labelCenterY);
      ctxY.rotate(-Math.PI / 2);

      ctxY.beginPath();
      ctxY.moveTo(-labelBoxWidth / 2 + borderRadius, -labelBoxHeight / 2);
      ctxY.lineTo(labelBoxWidth / 2 - borderRadius, -labelBoxHeight / 2);
      ctxY.quadraticCurveTo(
        labelBoxWidth / 2,
        -labelBoxHeight / 2,
        labelBoxWidth / 2,
        -labelBoxHeight / 2 + borderRadius,
      );
      ctxY.lineTo(labelBoxWidth / 2, labelBoxHeight / 2 - borderRadius);
      ctxY.quadraticCurveTo(
        labelBoxWidth / 2,
        labelBoxHeight / 2,
        labelBoxWidth / 2 - borderRadius,
        labelBoxHeight / 2,
      );
      ctxY.lineTo(-labelBoxWidth / 2 + borderRadius, labelBoxHeight / 2);
      ctxY.quadraticCurveTo(
        -labelBoxWidth / 2,
        labelBoxHeight / 2,
        -labelBoxWidth / 2,
        labelBoxHeight / 2 - borderRadius,
      );
      ctxY.lineTo(-labelBoxWidth / 2, -labelBoxHeight / 2 + borderRadius);
      ctxY.quadraticCurveTo(
        -labelBoxWidth / 2,
        -labelBoxHeight / 2,
        -labelBoxWidth / 2 + borderRadius,
        -labelBoxHeight / 2,
      );
      ctxY.closePath();

      ctxY.fillStyle = '#FFFFFF';
      ctxY.strokeStyle = '#E8EAEB';
      ctxY.lineWidth = 1;
      ctxY.fill();
      ctxY.stroke();

      ctxY.fillStyle = '#162C36';
      ctxY.textAlign = 'center';
      ctxY.textBaseline = 'middle';
      ctxY.fillText(labelText, 0, 1);

      const arrowBaseX = labelBoxWidth / 2 + 6;
      const arrowBaseY = 0;

      ctxY.beginPath();
      ctxY.moveTo(arrowBaseX, arrowBaseY);
      ctxY.lineTo(arrowBaseX + arrowTailLength, arrowBaseY);
      ctxY.strokeStyle = '#000';
      ctxY.lineWidth = 2;
      ctxY.stroke();
      ctxY.beginPath();
      ctxY.moveTo(arrowBaseX + arrowTailLength, -arrowSize / 2);
      ctxY.lineTo(arrowBaseX + arrowTailLength + arrowSize, 0);
      ctxY.lineTo(arrowBaseX + arrowTailLength, arrowSize / 2);
      ctxY.closePath();
      ctxY.fillStyle = '#000';
      ctxY.fill();

      ctxY.restore();
    },
  });

  const initiateUnderwritingChartsTooltip = (
    context: any,
  ): { tooltipEl: HTMLElement; tooltipModel: any } => {
    let tooltipEl = document.getElementById('chart-tooltip') as HTMLElement | null;

    const container = document.getElementById('pvx-roas-projection-container');

    if (container) {
      container.addEventListener('scroll', () => {
        if (tooltipEl) {
          tooltipEl.style.opacity = '0';
        }
      });
    }

    if (!tooltipEl) {
      tooltipEl = document.createElement('div');
      tooltipEl.id = 'chart-tooltip';
      tooltipEl.style.position = 'absolute';
      tooltipEl.style.background = '#fff';
      tooltipEl.style.color = '#162C36';
      tooltipEl.style.border = '1px solid #737F86';
      tooltipEl.style.borderRadius = '0.5rem';
      tooltipEl.style.padding = '8px';
      tooltipEl.style.boxShadow = '0px 4px 6px rgba(0,0,0,0.1)';
      tooltipEl.style.fontFamily = 'Manrope, sans-serif';
      tooltipEl.style.pointerEvents = 'none';
      tooltipEl.style.whiteSpace = 'nowrap';
      tooltipEl.style.opacity = '0';
      document.body.appendChild(tooltipEl);
    }

    const tooltipModel = context.tooltip;
    if (!tooltipModel || tooltipModel.opacity === 0) {
      tooltipEl.style.opacity = '0';
      return { tooltipEl, tooltipModel };
    }

    const chartCanvas = context.chart.canvas;
    const position = chartCanvas.getBoundingClientRect();
    const tooltipWidth = tooltipEl.offsetWidth;
    const tooltipHeight = tooltipEl.offsetHeight;
    const screenWidth = window.innerWidth;
    const screenHeight = window.innerHeight;

    let tooltipX = position.left + window.pageXOffset + tooltipModel.caretX;
    let tooltipY = position.top + window.pageYOffset + tooltipModel.caretY;

    if (tooltipX + tooltipWidth > screenWidth) {
      tooltipX -= tooltipWidth + 20;
    }
    if (tooltipX < 0) {
      tooltipX = 10;
    }
    if (tooltipY + tooltipHeight > screenHeight) {
      tooltipY -= tooltipHeight + 20;
    }
    if (tooltipY < 0) {
      tooltipY = 10;
    }

    tooltipEl.style.left = `${tooltipX}px`;
    tooltipEl.style.top = `${tooltipY}px`;
    tooltipEl.style.opacity = '1';

    return { tooltipEl, tooltipModel };
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      mode: 'index' as const,
      intersect: false,
    },
    layout: {
      padding: {
        bottom: 50,
        left: 50,
      },
    },
    plugins: {
      tooltip: {
        enabled: false,
        external: function (context: any) {
          const { tooltipEl, tooltipModel } = initiateUnderwritingChartsTooltip(context);

          const title = tooltipModel.title ? tooltipModel.title[0] : 'Unknown';
          const chartData = context.chart.data.datasets;
          const hoveredIndex = tooltipModel.dataPoints[0].dataIndex;
          const withoutFunding = chartData[1].data[hoveredIndex] || 0;
          const withFunding = chartData[0].data[hoveredIndex] || 0;

          let tooltipContent = `<div style="font-weight: bold; margin-bottom: 4px; font-size: 1rem;">${yLabel} : ${title}</div>`;

          tooltipContent += `
              <div style="padding-left: 8px; display: flex; align-items: center; justify-content: space-between; color: #6C6C6C; font-size: 0.875rem;">
                <span style="width: 10px; height: 10px; background-color: #5BA39D; border-radius: 50%; display: inline-block; margin-right: 8px;"></span>
                <div style="flex-grow: 1;">With PvX Funding</div>
                <div>${formatCurrency(withFunding, true, 1, 0)}</div>
              </div>
            `;
          tooltipContent += `
              <div style="padding-left: 8px; display: flex; align-items: center; justify-content: space-between; color: #6C6C6C; font-size: 0.875rem;">
                <span style="width: 10px; height: 10px; background-color: #1C3D5A; border-radius: 50%; display: inline-block; margin-right: 8px;"></span>
                <div style="flex-grow: 1;">Without PvX Funding</div>
                <div>${formatCurrency(withoutFunding, true, 1, 0)}</div>
              </div>
            `;

          tooltipEl.style.width = '300px';
          tooltipEl.style.height = 'auto';
          tooltipEl.style.padding = '12px';
          tooltipEl.style.background = '#fff';
          tooltipEl.style.borderRadius = '8px';

          tooltipEl.innerHTML = tooltipContent;
        },
      },
      legend: {
        display: true,
        position: 'bottom',
        align: 'start',
        labels: {
          boxWidth: 6,
          boxHeight: 6,
          borderRadius: 50,
          color: '#333',
          font: {
            family: 'Manrope, sans-serif',
            weight: '700',
          },
          usePointStyle: true,
          pointStyle: 'circle',
        },
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          callback: (val: any) => formatCurrency(val, true, 1, 0),
          color: '#989CA2',
          font: {
            family: 'Manrope',
            weight: 700,
          },
        },
        grid: {
          display: true,
          color: '#AECBD0',
          tickWidth: 0,
        },
        border: {
          color: '#AECBD0',
          dash: [5, 5],
        },
      },
      x: {
        grid: {
          display: false,
        },
        ticks: {
          color: '#989CA2',
          font: {
            family: 'Manrope',
            weight: 700,
          },
        },
        border: {
          color: '#AECBD0',
          dash: [5, 5],
        },
      },
    },
  };

  const hoverLinePlugin = {
    id: 'hoverLinePlugin',
    afterDraw: (chart: any) => {
      if (chart.tooltip && chart.tooltip._active && chart.tooltip._active.length) {
        const ctx = chart.ctx;
        const x = chart.tooltip._active[0].element.x;
        const topY = chart.chartArea.top;
        const bottomY = chart.chartArea.bottom;

        ctx.save();
        ctx.beginPath();
        ctx.setLineDash([5, 5]);
        ctx.moveTo(x, topY);
        ctx.lineTo(x, bottomY);
        ctx.lineWidth = 1;
        ctx.strokeStyle = '#999';
        ctx.stroke();
        ctx.restore();
      }
    },
  };

  return (
    <div style={{ height: '400px', width: '100%' }}>
      <Line
        ref={chartRef}
        data={data}
        options={options as any}
        plugins={[fillBetweenPlugin, customXAxisLabelPlugin(xLabel, yLabel), hoverLinePlugin]}
      />
    </div>
  );
};

export default CombinedProjectionsChart;
